import { RelatedApps, RELATED_BY_APP } from '@hypneuronerd/ui';
import type { LifetimeStats, SessionRecord } from '../data/types';
import { CATEGORY_LABELS } from '../data/types';
import styles from './Summary.module.css';

interface SummaryProps {
  session: SessionRecord;
  lifetime: LifetimeStats;
  onAnother: () => void;
}

export function Summary({ session, lifetime, onAnother }: SummaryProps) {
  const pct = Math.round((session.correct / session.total) * 100);
  const lifetimeAccuracy =
    lifetime.totalAnswered > 0 ? lifetime.totalCorrect / lifetime.totalAnswered : null;
  const thisRoundAccuracy = session.correct / session.total;
  const drift =
    lifetimeAccuracy !== null && lifetime.roundsPlayed > 1
      ? thisRoundAccuracy - lifetimeAccuracy
      : null;

  // Find weakest 2 categories by miss rate (excluding categories with no exposure)
  const weakest = Object.entries(session.byCategory)
    .filter(([, c]) => c.right + c.wrong > 0)
    .map(([cat, c]) => ({
      cat: cat as keyof typeof CATEGORY_LABELS,
      missRate: c.wrong / (c.right + c.wrong),
      total: c.right + c.wrong,
    }))
    .sort((a, b) => b.missRate - a.missRate || b.total - a.total)
    .slice(0, 3)
    .filter((c) => c.missRate > 0);

  return (
    <section className={styles.box}>
      <header className={styles.header}>
        <p className={styles.label}>round complete</p>
        <p className={styles.score}>
          {session.correct} of {session.total}
        </p>
        <p className={styles.pct}>{pct}%</p>
      </header>

      {weakest.length > 0 ? (
        <section className={styles.weakSection}>
          <h2 className={styles.sectionTitle}>patterns to keep watching for</h2>
          <ul className={styles.patternList}>
            {weakest.map((w) => (
              <li key={w.cat}>
                <span className={styles.patternName}>{CATEGORY_LABELS[w.cat]}</span>
                <span className={styles.patternMiss}>
                  {Math.round(w.missRate * 100)}% miss rate
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <p className={styles.cleanRun}>
          you cleared every pattern category. either a strong eye, or a quiet day at the table.
        </p>
      )}

      {/* Lifetime context — only shows after the second round, since
       * drift-from-average is meaningless on round one. */}
      {lifetime.roundsPlayed > 1 && lifetimeAccuracy !== null && (
        <p className={styles.lifetimeContext}>
          your lifetime average across <strong>{lifetime.roundsPlayed}</strong> rounds:{' '}
          <strong>{Math.round(lifetimeAccuracy * 100)}%</strong>
          {drift !== null && Math.abs(drift) >= 0.05 && (
            <>
              {' '}({drift > 0 ? '+' : ''}
              {Math.round(drift * 100)}% this round)
            </>
          )}
        </p>
      )}

      <p className={styles.realityCheck}>
        real-world judgment requires more context than a single card. this is
        practice — pattern recognition, not threat detection.
      </p>

      <button type="button" className={styles.again} onClick={onAnother}>
        play again
      </button>

      <RelatedApps apps={RELATED_BY_APP['red-flag-green-flag']} surface="midnight" />
    </section>
  );
}
